import React from 'react';
import { Icon, StackLayout, UtilityText } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useGetUserRewardsAccounts } from '../../hooks/use-get-user-rewards-accounts.hook';

export interface UserRewardsAccountsListProps {
  /** Optional CSS styles to apply to the container */
  css?: any;
}

/**
 * Component that displays user rewards accounts in a horizontal list.
 *
 * Each reward account shows the balance using a PointsIcon component.
 * The icon is subdued when either the reward account or user reward account is inactive.
 *
 * @example
 * <UserRewardsAccountsList />
 */
export const UserRewardsAccountsList = ({
  css,
}: UserRewardsAccountsListProps) => {
  const { rewardAccounts, isLoading, isError } = useGetUserRewardsAccounts();
  const { formatNumber } = useIntl();

  if (isLoading) {
    return null; // or loading state
  }

  if (isError || !rewardAccounts?.data) {
    return null; // or error state
  }

  return (
    <StackLayout
      orientation="horizontal"
      spacing="$one"
      verticalAlignment="center"
      css={css}
      className="reward-accounts-list"
    >
      {rewardAccounts.data.map((userAccount) => {
        const rewardsAccount = userAccount.rewardsAccount.data;
        const isInactive =
          userAccount.status === 'inactive' ||
          rewardsAccount.status === 'inactive';

        if (isInactive) return null;

        return (
          <StackLayout
            key={userAccount.id}
            orientation="horizontal"
            spacing="$quarter"
            verticalAlignment="center"
            css={{
              maxHeight: 'fit-content',
              paddingX: '$one',
              paddingY: '$half',
              borderRadius: '$circle',
              backgroundColor: '$onSurfaceIconPrimary',
            }}
          >
            <Icon size={12} icon={rewardsAccount.image} />
            <UtilityText css={{ color: '$onSurfaceTextReversed' }}>
              {` ${formatNumber(userAccount.balance)} ${rewardsAccount.unit}`}
            </UtilityText>
          </StackLayout>
        );
      })}
    </StackLayout>
  );
};
